import { apiFetch } from './project.js';

/**
 * Asking about the project (prd-deltas §24): its TL;DR, and the chat. Every refusal
 * carries a message written for people, which the page shows as it is.
 */

/** The TL;DR's four points, in order (prd-deltas §26); one the reading could not say is absent. */
export const TLDR_POINTS = [
  { key: 'idea', label: 'Idea' },
  { key: 'flow', label: 'Flow' },
  { key: 'stack', label: 'Framework & language' },
  { key: 'infra', label: 'Infrastructure' },
] as const;

export type TldrPoint = (typeof TLDR_POINTS)[number]['key'];

/** How much has changed since a kept TL;DR was written, and whether to write it again (§27). */
export interface TldrFreshness {
  suggest: 'fresh' | 'maybe' | 'rescan';
  message: string;
}

export interface Tldr {
  points: Partial<Record<TldrPoint, string>>;
  /** When it was written; absent when there was nothing to write. */
  writtenAt?: string;
  freshness?: TldrFreshness;
  /** The documents it was written from. */
  basis: string[];
  /** Said instead of sentences when there is nothing to summarise. */
  message?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Files the model read to answer. */
  reads?: string[];
}

export interface Chat {
  chatId: string | null;
  messages: ChatMessage[];
}

async function json<T>(response: Response): Promise<T> {
  if (response.ok) return (await response.json()) as T;
  let message = `The server answered ${response.status}.`;
  try {
    const body = (await response.json()) as { message?: string };
    if (body.message) message = body.message;
  } catch { /* the status says enough */ }
  throw new Error(message);
}

const base = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}`;

/** The kept TL;DR, written first if there is none; `write` writes it again regardless. */
export const fetchTldr = async (projectId: string, write = false): Promise<Tldr> =>
  json<Tldr>(await apiFetch(`${base(projectId)}/tldr`, write ? { method: 'POST' } : {}));

export const fetchChat = async (projectId: string, signal?: AbortSignal): Promise<Chat> =>
  json<Chat>(await apiFetch(`${base(projectId)}/chat`, { signal: signal ?? null }));

export const startNewChat = async (projectId: string): Promise<Chat & { effect: string }> =>
  json(await apiFetch(`${base(projectId)}/chat/new`, { method: 'POST' }));

export const askChat = async (
  projectId: string,
  text: string,
  chatId: string | null,
): Promise<{ chatId: string; answer: ChatMessage; effect: string }> =>
  json(
    await apiFetch(`${base(projectId)}/chat`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ text, ...(chatId ? { chatId } : {}) }),
    }),
  );
